import type { CashOperationResponse } from './gestorEfectivo.interface';

/**
 * CashKeeper error code/message mapping to i18n keys (or readable fallback messages).
 */
const ERROR_CODE_MAP: Record<string, string> = {
  'ERR:DESCONECTADO': 'CASHKEEPER_ERROR_DESCONECTADO',
  'ERR:NO CONECTADO': 'CASHKEEPER_ERROR_DESCONECTADO',
  'ERR:TIMEOUT': 'CASHKEEPER_ERROR_TIMEOUT',
  'ERR:COMUNICACION': 'CASHKEEPER_ERROR_COMUNICACION',
  'ERR:CANCELADO': 'CASHKEEPER_ERROR_CANCELADO',
  'ERR:RECHAZADO': 'CASHKEEPER_ERROR_RECHAZADO',
  'ERR:IMPORTE': 'CASHKEEPER_ERROR_IMPORTE',
  'ERR:OPERACION': 'CASHKEEPER_ERROR_OPERACION',
  'ERR:GENERICO': 'CASHKEEPER_ERROR_GENERICO',
};

/** Default translation key when no mapping exists. */
export const CASHKEEPER_ERROR_DEFAULT = 'CASHKEEPER_ERROR_GENERICO';

/**
 * Extract `errCode` and `errDesc` when the format is:
 * `ERR:{errCode}-{errDesc}` (e.g. `ERR:001-Communication...`).
 * The `ERR:` prefix might be optional depending on the flow.
 */
export function extractCodeAndDescriptionCashKeeper(
  error: string | undefined
): { code?: number; desc?: string } {
  if (!error || typeof error !== 'string') return {};
  const m = error
    .trim()
    .match(/^(?:ERR:)?\s*(\d+)\s*-\s*(.+)\s*$/i);
  if (!m) return {};
  const code = parseInt(m[1], 10);
  const desc = (m[2] || '').trim();
  return Number.isNaN(code) ? { desc } : { code, desc };
}

/** Return only `errDesc` for fallback (when no i18n key exists). */
export function extractDescriptionCashKeeper(
  error: string | undefined
): string | undefined {
  return extractCodeAndDescriptionCashKeeper(error).desc;
}

/** Return the error message when the response is an error (ok: false). */
export function getErrorMessage(
  res: CashOperationResponse
): string | undefined {
  return res.ok ? undefined : (res as { error: string }).error;
}

/**
 * Normalize the error message into an i18n translation key (or the default key).
 */
export function normalizeCashKeeperError(error: string | undefined): string {
  if (!error || typeof error !== 'string') {
    return CASHKEEPER_ERROR_DEFAULT;
  }
  // Explicit vendor anomaly: "... Error:0-" should always be generic.
  if (/\bERROR:\s*0\s*-\s*$/i.test(error.trim()) || /\bERROR:\s*0\s*-/i.test(error)) {
    return CASHKEEPER_ERROR_DEFAULT;
  }
  // 1) New format: {errCode}-{errDesc} or ERR:{errCode}-{errDesc} (includes leading zeros)
  const { code } = extractCodeAndDescriptionCashKeeper(error);
  if (code !== undefined) {
    if (code === 0) {
      return CASHKEEPER_ERROR_DEFAULT;
    }
    return `CASHKEEPER_ERROR_CODE_${code}`;
  }
  const upper = error.trim().toUpperCase();
  for (const [errorCodeCandidate, translationKey] of Object.entries(ERROR_CODE_MAP)) {
    if (upper.startsWith(errorCodeCandidate)) {
      return translationKey;
    }
  }
  // If it already looks like a key, return it; otherwise return generic.
  if (error.startsWith('ERR:')) {
    const translationKey =
      'CASHKEEPER_ERROR_' +
      upper.replace(/^ERR:?\s*/, '').replace(/\s+/g, '_').slice(0, 30);
    return translationKey;
  }
  return CASHKEEPER_ERROR_DEFAULT;
}
